// =====================================================================
//  Dialogue « Skill IA »
//  ---------------------------------------------------------------------
//  Met à disposition le skill easymail-templates, qui permet à l'agent IA
//  du collaborateur de préparer les templates et stratégies OTP sous forme
//  de paquets à importer ici. Deux téléchargements (SkillController) :
//  le ZIP (Claude) et un fichier unique (autres IA). La procédure complète
//  est dans le README du dépôt du skill (Skill:DocumentationUrl).
// =====================================================================

import {JsonFetchJson} from "../../Helper/FetchHelper.js";
import {NotificationHelper} from "../../Helper/NotificationHelper.js";

interface ISkillInfo {
    Nom: string;
    Version: string;
    DocumentationUrl?: string;
}

export class SkillDialog {
    #kWindow: kendo.ui.Window | undefined;

    public static Open = async (): Promise<void> => {
        let info: ISkillInfo | undefined;
        try {
            info = await JsonFetchJson.Get<ISkillInfo>("/skill/info");
        } catch {
            info = undefined;
        }
        if (!info?.Version) {
            NotificationHelper.Instance.error("Le skill n'est pas disponible sur ce Backoffice.");
            return;
        }
        new SkillDialog().#open(info);
    };

    #open = (info: ISkillInfo) => {
        this.#kWindow = this.#buildHtml(info).appendTo(document.body).kendoWindow({
            title: `Skill IA — ${info.Nom} ${info.Version}`,
            modal: true,
            width: "640px",
            resizable: false,
            actions: ["Close"],
            deactivate: () => this.#kWindow?.destroy(),
        }).data("kendoWindow");
        this.#kWindow.center().open();
    };

    #buildHtml = (info: ISkillInfo): JQuery => {
        const root = $("<div></div>").css({display: "flex", flexDirection: "column", gap: "10px", lineHeight: "1.45"});

        $("<p></p>").css({margin: 0}).text(
            "Ce skill apprend à votre agent IA à rédiger les templates et stratégies OTP d'EASYMAIL. " +
            "Il produit un paquet .easymail.json que vous importez ici (bouton « Importer »), puis vous prévisualisez et enregistrez."
        ).appendTo(root);

        const steps = $("<ul></ul>").css({margin: 0, paddingLeft: "20px"}).appendTo(root);
        SkillDialog.#step(steps, "claude.ai / Claude Desktop",
            "activer l'exécution de code (Paramètres › Fonctionnalités), puis Personnaliser › Skills › « + » › importer le ZIP.");
        SkillDialog.#step(steps, "Claude Code",
            "décompresser le ZIP dans ~/.claude/skills/ (dossier easymail-templates).");
        SkillDialog.#step(steps, "Autre IA",
            "joindre le fichier unique en entier comme instructions ou connaissances du projet.");

        $("<p></p>").css({margin: 0}).text(
            "Nouvelle version : réimportez le ZIP (ou remplacez le dossier). Ne collez jamais la clé du compte de service dans la conversation."
        ).appendTo(root);

        if (info.DocumentationUrl)
            $("<a target='_blank' rel='noopener'></a>").attr("href", info.DocumentationUrl)
                .text("Documentation complète (README du dépôt du skill)")
                .appendTo($("<p></p>").css({margin: 0}).appendTo(root));

        const footer = $("<div></div>").css({display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "6px"}).appendTo(root);
        SkillDialog.#link("Fichier unique (autres IA)", "/skill/fichier-unique", "base").appendTo(footer);
        SkillDialog.#link(`Télécharger le ZIP (${info.Version})`, "/skill/zip", "primary").appendTo(footer);
        $("<button type='button'></button>").addClass("k-button k-button-md k-rounded-md k-button-solid k-button-solid-base")
            .text("Fermer").on("click", () => this.#kWindow?.close()).appendTo(footer);

        return $("<div></div>").append(root);
    };

    static #step = (list: JQuery, outil: string, texte: string) =>
        $("<li></li>").append($("<strong></strong>").text(outil + " : ")).append(document.createTextNode(texte)).appendTo(list);

    static #link = (text: string, href: string, kind: "base" | "primary"): JQuery =>
        $("<a></a>").attr({href, download: ""})
            .addClass(`k-button k-button-md k-rounded-md k-button-solid k-button-solid-${kind}`)
            .text(text);
}
